<template>
  <MainLayout :showNavigation="false">

    <div class="device-check-wrapper d-flex flex-column align-center justify-center">
      <h1 class="device-check-title">Are you on the device you'll use to record?</h1>
      <v-alert
        type="warning"
        outlined
        dense
        class="mb-6 requirement-alert">
        Single device recording requires the OpenCap App Store app version 2.0 or newer. <a class="app-store-link" href="https://apps.apple.com/us/app/opencap/id1630513242" target="_blank" rel="noopener noreferrer">Get it on the App Store</a>.
      </v-alert>

      <div
        v-if="isIosDevice"
        class="options-container">
        <div
          class="option-card pa-6 d-flex flex-column align-center"
          @click="onThisDevice">
          <v-icon size="64" class="mb-4 option-icon">mdi-check-circle-outline</v-icon>
          <div class="title-row mb-2">
            <h2 class="mb-0 option-title">Yes, this device</h2>
          </div>
          <p class="text-center option-description">
            I'll record using this phone. Set up the session here and open in the app.
          </p>
          <v-btn
            class="mt-4 select-button"
            large
            :loading="loading"
            :disabled="loading">
            Select
          </v-btn>
        </div>

        <div
          class="option-card pa-6 d-flex flex-column align-center"
          @click="onDifferentDevice">
          <v-icon size="64" class="mb-4 option-icon">mdi-qrcode-scan</v-icon>
          <div class="title-row mb-2">
            <h2 class="mb-0 option-title">No, different device</h2>
          </div>
          <p class="text-center option-description">
            I'll use a different phone to record. Show me a QR code to connect it.
          </p>
          <v-btn
            class="mt-4 select-button"
            large
            :loading="loading"
            :disabled="loading">
            Select
          </v-btn>
        </div>
      </div>

      <div
        v-else
        class="non-ios-message mt-6">
        <v-alert
          type="info"
          outlined
          dense
          class="mb-4 non-ios-alert">
          Monocular recording is supported on iPhone and iPad using the OpenCap app. You can open this page on a compatible iOS device, or set up the session here and scan the QR code with your phone to connect. Alternatively, use the multi-camera workflowi.
        </v-alert>
        <v-btn
          class="mt-2"
          @click="onDifferentDevice">
          Set up session and show QR code
        </v-btn>
      </div>

      <v-btn text class="device-check-back" @click="$router.push({ name: 'RecordingMode' })">
        <v-icon left>mdi-arrow-left</v-icon>
        {{ backLabel }}
      </v-btn>
    </div>
  </MainLayout>
</template>

<script>
import { mapMutations, mapActions, mapState } from 'vuex'
import { apiError } from '@/util/ErrorMessage.js'
import MainLayout from '@/layout/MainLayout'

export default {
  name: 'DeviceCheck',
  components: {
    MainLayout
  },
  data() {
    return {
      loading: false
    }
  },
  computed: {
    ...mapState({
      session: state => state.data.session
    }),
    backLabel() {
      return this.$vuetify.breakpoint.smAndDown ? 'Back' : 'Back to recording mode'
    },
    isIosDevice() {
      // Detect if the current device is an iPhone or iPad (not desktop with emulated/resized viewport)
      if (typeof navigator === 'undefined') return false

      const ua = navigator.userAgent || ''
      const isIOS = /iPhone|iPad|iPod/i.test(ua)

      // iPadOS 13+ identifies as Mac; check for touch support (excludes Mac with trackpad)
      const isIPadOS =
        !isIOS &&
        navigator.platform === 'MacIntel' &&
        typeof navigator.maxTouchPoints === 'number' &&
        navigator.maxTouchPoints > 1

      const uaSaysIos = isIOS || isIPadOS
      if (!uaSaysIos) return false

      // Only treat as iOS if the primary input is touch (pointer: coarse).
      // Excludes desktop browsers with device emulation or resized to iPhone size.
      const hasTouchPrimary =
        typeof window !== 'undefined' &&
        window.matchMedia('(pointer: coarse)').matches

      return uaSaysIos && hasTouchPrimary
    }
  },
  methods: {
    ...mapMutations('data', ['clearAll']),
    ...mapActions('data', ['initSession']),
    async onThisDevice() {
      // Create a new session, then go to Neutral in monocular mode
      this.loading = true
      try {
        this.clearAll()
        await this.initSession()
        this.$router.push({
          name: 'Neutral',
          params: { id: this.session.id },
          query: { isMono: 'true', fromDevice: 'true' }
        })
      } catch (error) {
        apiError(error)
      } finally {
        this.loading = false
      }
    },
    onDifferentDevice() {
      // Go to ConnectDevices with isMono flag — user will scan QR from phone
      this.$router.push({
        name: 'ConnectDevices',
        query: { isMono: 'true', fromDeviceCheck: 'true' }
      })
    }
  }
}
</script>

<style lang="scss" scoped>
.device-check-wrapper {
  width: 100%;
  max-width: 980px;
  margin: 0 auto;
  min-height: 60vh;
  padding: 12px 16px 24px 16px;

  a {
    text-decoration: none !important;
    color: var(--app-text-muted);

    &:hover {
      text-decoration: underline !important;
      color: var(--app-text-primary);
    }
  }
}

.device-check-title {
  font-size: 1.5rem;
  font-weight: 600;
  color: var(--app-text-primary);
  text-align: center;
  margin: 0 0 32px 0;
}

.requirement-alert {
  width: 100%;
  max-width: 920px;
}

.app-store-link {
  color: var(--app-warning-link) !important;
  text-decoration: underline;
  text-underline-offset: 2px;
}

.options-container {
  width: 100%;
  max-width: 920px;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
  align-items: stretch;
}

.option-card {
  width: 100%;
  min-height: 280px;
  cursor: pointer;
  transition: transform 0.2s, box-shadow 0.2s;

  display: flex;
  flex-direction: column;
  justify-content: space-between;

  background: var(--app-surface);
  border: 1px solid var(--app-border);
  border-radius: 12px;
  box-shadow: var(--app-shadow);

  &:hover {
    transform: translateY(-4px);
    box-shadow: 0 12px 40px rgba(0, 0, 0, 0.5);
  }
}

.option-description {
  font-size: 0.95rem;
  color: var(--app-text-muted);
  max-width: 320px;
  width: 100%;
}

.title-row {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 36px;
  width: 100%;
}

.option-card,
.option-card .option-title {
  color: var(--app-text-primary);
  font-size: 1.25rem;
  text-align: center;
}

.option-icon {
  color: var(--app-text-primary) !important;
}

.select-button {
  min-width: 120px;
  background: var(--app-action-bg) !important;
  color: var(--app-action-text) !important;
  text-transform: none;
  font-weight: 600;
}

@media (max-width: 959px) {
  .device-check-wrapper {
    padding: 8px 12px 20px 12px;
  }

  .device-check-title {
    font-size: 1.25rem;
    margin-bottom: 24px;
  }

  .options-container {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    max-width: 100%;
    gap: 10px;
  }

  .option-card {
    min-height: 220px;
    min-width: 0;
    padding: 14px !important;
  }

  .option-card .option-title {
    font-size: 1.05rem;
  }

  .option-description {
    font-size: 0.85rem;
  }
}

@media (max-width: 599px) {
  .device-check-wrapper {
    height: 100%;
    min-height: 0;
    padding: 4px 4px 8px;
    justify-content: flex-start !important;
  }

  .device-check-title {
    flex: 0 0 auto;
    font-size: 1.15rem;
    line-height: 1.25;
    margin-bottom: 10px;
  }

  .requirement-alert {
    flex: 0 0 auto;
    margin-bottom: 10px !important;
    padding: 8px 10px !important;
    font-size: 0.875rem;
    line-height: 1.35;

    ::v-deep .v-alert__icon {
      margin-right: 8px;
    }
  }

  .options-container {
    flex: 0 0 auto;
    gap: 8px;
  }

  .option-card {
    height: auto;
    min-height: 260px;
    padding: 10px 8px !important;
  }

  .option-icon {
    font-size: 48px !important;
    margin-bottom: 8px !important;
  }

  .option-card .option-title {
    font-size: 0.95rem;
    line-height: 1.3;
  }

  .title-row {
    min-height: 48px;
    margin-bottom: 6px !important;
  }

  .option-description {
    font-size: 0.8rem;
    line-height: 1.35;
    margin-bottom: 0;
  }

  .select-button {
    min-width: 84px;
    height: 40px !important;
    min-height: 40px !important;
    margin-top: 8px !important;
    padding: 0 12px !important;
  }

  .device-check-back {
    flex: 0 0 auto;
    height: 40px !important;
    min-height: 40px !important;
    margin-top: 8px !important;
  }
}

@media (max-width: 599px) and (max-height: 700px) {
  .device-check-title {
    font-size: 1.05rem;
    margin-bottom: 8px;
  }

  .requirement-alert {
    margin-bottom: 8px !important;
    padding: 6px 8px !important;
    font-size: 0.8rem;
  }

  .option-card {
    min-height: 220px;
    padding: 8px 6px !important;
  }

  .option-icon {
    font-size: 40px !important;
    margin-bottom: 4px !important;
  }

  .option-card .option-title {
    font-size: 0.875rem;
  }

  .title-row {
    min-height: 42px;
    margin-bottom: 4px !important;
  }

  .option-description {
    font-size: 0.75rem;
    line-height: 1.25;
  }

  .select-button,
  .device-check-back {
    height: 36px !important;
    min-height: 36px !important;
  }
}
</style>
